import {
  EditableFrequency,
  Schedule,
  DateStampString,
  TwiceMonthlyDays,
  WeeklyFrequency,
  MonthlyFrequency,
  TwiceMonthlyFrequency,
} from "source/giving/donate/form/types"
import {
  WEEK_DAY_LABELS,
  MONTH_DAY_LABELS,
  TWICE_MONTHLY_LABELS,
} from "source/giving/donate/form/constants"
import { RecurringDonation, RecurringDonationStatus } from "../types"

export const StatusOptions = [
  { label: "Active", value: "active" as RecurringDonationStatus },
  {
    label: "On hold indefinitely",
    value: "indefinite_hold" as RecurringDonationStatus,
  },
  { label: "On hold through", value: "hold_until" as RecurringDonationStatus },
]

export const FrequencyTypeOptions = [
  { label: "Monthly", value: "monthly" as EditableFrequency["type"] },
  { label: "Weekly", value: "weekly" as EditableFrequency["type"] },
  {
    label: "Every other week",
    value: "every_other_week" as EditableFrequency["type"],
  },
  {
    label: "Twice monthly",
    value: "twice_monthly" as EditableFrequency["type"],
  },
]

export const WeekdayOptions = Object.entries(WEEK_DAY_LABELS).map(
  ([dayNum, label]) => ({
    label,
    value: dayNum.toString(),
  }),
)

export const MonthDayOptions = Object.entries(MONTH_DAY_LABELS).map(
  ([dayNum, label]) => ({
    label,
    value: dayNum.toString(),
  }),
)

export const TwiceMonthlyOptions = Object.entries(TWICE_MONTHLY_LABELS).map(
  ([dayNum, label]) => ({
    label,
    value: dayNum.toString(),
  }),
)

export function getInitialStatus(
  recurringDonation: RecurringDonation,
): RecurringDonationStatus {
  if (recurringDonation.on_hold_indefinitely) return "indefinite_hold"
  if (
    recurringDonation.release_hold_at &&
    new Date(recurringDonation.release_hold_at) > new Date()
  )
    return "hold_until"
  return "active"
}

export function scheduleToEditableFrequency(
  schedule: Schedule,
  releaseHoldAt: DateStampString,
): EditableFrequency {
  if ("day_in_month" in schedule) {
    return {
      type: "monthly",
      starting: releaseHoldAt,
      day: schedule.day_in_month.day,
    }
  }
  if ("weekday" in schedule) {
    return {
      type: "weekly",
      starting: releaseHoldAt,
      weekday: schedule.weekday.weekday,
    }
  }
  if ("biweekly" in schedule) {
    return {
      type: "every_other_week",
      starting: schedule.biweekly.start_after,
      weekday: schedule.biweekly.weekday,
    }
  }
  if ("union" in schedule) {
    const [firstDay, secondDay] = schedule.union
    const dayOne = firstDay.day_in_month.day
    const dayTwo = secondDay.day_in_month.day

    const daysKey = [dayOne, dayTwo].join("-")

    if (daysKey in TWICE_MONTHLY_LABELS) {
      return {
        type: "twice_monthly",
        starting: releaseHoldAt,
        days: daysKey as TwiceMonthlyDays,
      }
    }
  }

  throw new Error("Invalid schedule")
}

export function setInitialFrequencyValues(frequency: EditableFrequency): void {
  if (frequency.type === "monthly" && frequency.day === undefined) {
    frequency.day = parseInt(
      MonthDayOptions[0].value,
    ) as MonthlyFrequency["day"]
  }
  if (
    frequency.type === "weekly" ||
    (frequency.type === "every_other_week" && frequency.weekday === undefined)
  ) {
    frequency.weekday = parseInt(
      WeekdayOptions[0].value,
    ) as WeeklyFrequency["weekday"]
  }
  if (frequency.type === "twice_monthly" && frequency.days === undefined) {
    frequency.days = TwiceMonthlyOptions[0]
      .value as TwiceMonthlyFrequency["days"]
  }
}

export function scheduleStateToAttributes(
  recurringDonation: RecurringDonation,
  schedule: Schedule,
  status: RecurringDonationStatus,
  releaseHoldAt: string | null,
  nextOccurrence: string | null,
): RecurringDonation {
  recurringDonation.next_occurrence = nextOccurrence
  recurringDonation.schedule = schedule

  if (status === "indefinite_hold") {
    recurringDonation.on_hold_indefinitely = true
  } else {
    recurringDonation.on_hold_indefinitely = false
  }
  if (status === "active") {
    recurringDonation.release_hold_at = null
  }

  if (status === "hold_until" && releaseHoldAt !== null) {
    recurringDonation.release_hold_at = releaseHoldAt
  }
  return recurringDonation
}
